import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardHeader,
  Button
} from "reactstrap";

// ---------- react-beautiful-dnd imports ----------
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Existing modals
import AddTpcCourseCategoryModal from 'components/Modal/AddTpcCourseCategoryModal';
import AddTpcCourseCategoryLessonsModal from 'components/Modal/AddTpcCourseCategoryLessonsModal';
import AddTpcCourseSubCategoryModal from 'components/Modal/AddTpcCourseSubCategoryModal';

// Redux
import { connect, useDispatch } from 'react-redux';
import {
  updateTpcCourseCategoryOrder,
  deleteTpcCourseLessons,
  deleteTpcCourseCategory,
  deleteTpcCourseLessonsReset,
  deleteTpcCourseCategoryReset,
  fetchTpcCourses
} from 'redux/ManageTpcCourses/ManageTpcCoursesActions';

import Swal from 'sweetalert2';

const ManageTpcCoursesLessons = (props) => {
  const dispatch = useDispatch();

  // ------------- State -------------
  const [items, setItems] = useState([]); // top-level categories
  const [modalAddTpcCourseCategoryModal, setModalAddTpcCourseCategoryModal] = useState(false);
  const [modalAddTpcCourseCategoryLessonsModal, setModalAddTpcCourseCategoryLessonsModal] = useState(false);
  const [modalAddTpcCourseSubcategory, setModalAddTpcCourseSubcategory] = useState(false);

  // Flags
  const [editCourseCategory, setEditCourseCategory] = useState(false);
  const [editCourseCategoryLessons, setEditCourseCategoryLessons] = useState(false);
  const [editSubcategory, setEditSubcategory] = useState(false);

  // IDs
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [parentCategoryId, setParentCategoryId] = useState(null);

  // For editing
  const [categoryToUpdate, setCategoryToUpdate] = useState({});
  const [lessonToUpdate, setLessonToUpdate] = useState({});
  const [subcategoryToUpdate, setSubcategoryToUpdate] = useState({});

  // For storing the entire course props
  const [courseProps, setCourseProps] = useState(props.courseData);

  // Where to create the lesson? ("category" or "subcategory")
  const [createLessonTo, setCreateLessonTo] = useState("category");
  const [selectedCategory, setSelectedCategory] = useState(false);

  // ----------- Modal Toggles -----------
  const toggleAddTpcCourseCategoryModal = () => {
    setModalAddTpcCourseCategoryModal(!modalAddTpcCourseCategoryModal);
  };
  const toggleAddTpcCourseCategoryLessonsModal = () => {
    setModalAddTpcCourseCategoryLessonsModal(!modalAddTpcCourseCategoryLessonsModal);
  };
  const toggleAddTpcCourseSubcategoryModal = () => {
    setModalAddTpcCourseSubcategory(!modalAddTpcCourseSubcategory);
  };

  // ----------- Load Data Effects -----------
  useEffect(() => {
    if (props.createTpcCourseCategory.tpccourse.length) {
      setItems(props.createTpcCourseCategory.tpccourse[0].category);
    }
  }, [props.createTpcCourseCategory]);

  useEffect(() => {
    if (props.fetchTpcCourses.tpccourses.length) {
      const foundCourse = props.fetchTpcCourses.tpccourses.find(
        (course) => course.tpcCourseId === props.courseData.tpcCourseId
      );
      if (foundCourse && foundCourse.category) {
        setItems(foundCourse.category);
      } else {
        setItems([]);
      }
    }
  }, [props.fetchTpcCourses, props.courseData]);

  // ---------- Category CRUD -----------
  const handleCreateCategory = () => {
    setEditCourseCategory(false);
    setCategoryToUpdate({});
    toggleAddTpcCourseCategoryModal();
  };

  const handleUpdateCategory = (e, value) => {
    e.preventDefault();
    setEditCourseCategory(true);
    setCategoryToUpdate(value);
    toggleAddTpcCourseCategoryModal();
  };

  const handleDeleteTpcCourseCategory = (e, categoryId) => {
    e.preventDefault();
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        let categories = [...items];
        let updated = categories.filter((category) => category.id !== categoryId);
        updated.forEach((cat, i) => cat.id = i + 1);

        dispatch(
          deleteTpcCourseCategory({
            handle: props.userDetails.handle,
            categories: updated,
            tpcCourseId: props.courseData.tpcCourseId
          })
        );
      }
    });
  };

  useEffect(() => {
    if (
      props.deleteTpcCourseCategory.tpccourse.length &&
      typeof props.deleteTpcCourseCategory.error === "undefined"
    ) {
      Swal.fire({
        title: 'Success!',
        text: 'Successfully deleted course category!',
        icon: 'success',
        timer: 3000
      }).then(() => {
        dispatch(fetchTpcCourses({}));
        dispatch(deleteTpcCourseCategoryReset());
      });
    } else if (typeof props.deleteTpcCourseCategory.error !== "undefined") {
      Swal.fire({
        title: 'Failed!',
        text: 'Failed to delete course category!',
        icon: 'error',
        timer: 3000
      }).then(() => {
        dispatch(fetchTpcCourses({}));
        dispatch(deleteTpcCourseCategoryReset());
      });
    }
  }, [props.deleteTpcCourseCategory, dispatch]);

  // ---------- Subcategory CRUD -----------
  const handleAddSubcategory = (category) => {
    setEditSubcategory(false);
    setSubcategoryToUpdate({});
    setParentCategoryId(category.id);
    toggleAddTpcCourseSubcategoryModal();
  };

  const handleEditSubcategory = (subCat, parentCatId) => {
    setEditSubcategory(true);
    setSubcategoryToUpdate(subCat);
    setParentCategoryId(parentCatId);
    toggleAddTpcCourseSubcategoryModal();
  };

  const handleDeleteSubcategory = (subCat, parentCatId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        let newItems = [...items];
        let catIndex = newItems.findIndex((c) => c.id === parentCatId);
        if (catIndex !== -1 && newItems[catIndex].subCategories) {
          let updatedSub = newItems[catIndex].subCategories.filter((s) => s.id !== subCat.id);
          updatedSub.forEach((sc, i) => sc.id = i + 1);
          newItems[catIndex].subCategories = updatedSub;
        }

        dispatch(
          deleteTpcCourseCategory({
            handle: props.userDetails.handle,
            categories: newItems,
            tpcCourseId: props.courseData.tpcCourseId
          })
        );
      }
    });
  };

  // ---------- Lesson CRUD -----------
  const handleCreateLesson = (e, categoryOrSubcategory, nameCreateLessonTo, parentCatId) => {
    e.preventDefault();
    setEditCourseCategoryLessons(false);
    setEditCourseCategory(false);
    setCreateLessonTo(nameCreateLessonTo);
    setSelectedCategory(categoryOrSubcategory);

    setCategoryId(categoryOrSubcategory.id);
    setParentCategoryId(parentCatId);

    toggleAddTpcCourseCategoryLessonsModal();
  };

  const handleUpdateLesson = (e, lessonObj, parentId, isSubcategory, subCatId) => {
    e.preventDefault();
    setSubCategoryId(subCatId);
    setCreateLessonTo(isSubcategory ? "subcategory" : "category");

    lessonObj.categoryId = parentId;
    setCategoryId(parentId);
    setEditCourseCategoryLessons(true);
    setLessonToUpdate(lessonObj);
    setParentCategoryId(parentId);
    toggleAddTpcCourseCategoryLessonsModal();
  };

  const handleDeleteTpcCourseLesson = (e, lessonValue, catId, isSubcategory = false, subCatId) => {
    e.preventDefault();
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.showLoading();
        let categories = [...items];
        let category = categories.find((c) => c.id == catId);
        if (isSubcategory && category) {
          category = category.subCategories.find((sc) => sc.id == subCatId);
        }
        if (!category) return;

        category.lessons = category.lessons.filter((l) => l.id !== lessonValue.id);

        dispatch(
          deleteTpcCourseLessons({
            handle: props.userDetails.handle,
            categories: categories,
            tpcCourseId: props.courseData.tpcCourseId
          })
        );
      }
    });
  };

  useEffect(() => {
    if (props.deleteTpcCourseLessons.tpccourse.length && typeof props.deleteTpcCourseLessons.error === "undefined") {
      Swal.fire({
        title: 'Success!',
        text: 'Successfully deleted course lesson!',
        icon: 'success',
        timer: 3000
      }).then(() => {
        dispatch(fetchTpcCourses({}));
        dispatch(deleteTpcCourseLessonsReset());
      });
    } else if (typeof props.deleteTpcCourseLessons.error !== "undefined") {
      Swal.fire({
        title: 'Failed!',
        text: 'Failed to delete course lesson!',
        icon: 'error',
        timer: 3000
      }).then(() => {
        dispatch(fetchTpcCourses({}));
        dispatch(deleteTpcCourseLessonsReset());
      });
    }
  }, [props.deleteTpcCourseLessons, dispatch]);

  // ---------- onDragEnd for react-beautiful-dnd -----------
  const onDragEnd = (result) => {
    const { source, destination, draggableId, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    if (type === "CATEGORY") {
        let newItems = Array.from(items);
        const [moved] = newItems.splice(source.index, 1);
        newItems.splice(destination.index, 0, moved);
        newItems.forEach((cat, i) => cat.id = i + 1);
        setItems(newItems);

        dispatch(updateTpcCourseCategoryOrder({
            category: newItems,
            tpcCourseId: props.courseData.tpcCourseId,
            handle: props.userDetails.handle
        }));
    }
    else if (type.startsWith("SUBCAT-")) {
        const catId = type.split("-")[1];
        let newItems = [...items];
        const catIndex = newItems.findIndex((c) => String(c.id) === String(catId));
        if (catIndex === -1) return;

        let subCats = Array.from(newItems[catIndex].subCategories || []);
        const [moved] = subCats.splice(source.index, 1);
        subCats.splice(destination.index, 0, moved);
        subCats.forEach((sc, i) => sc.id = i + 1);
        newItems[catIndex].subCategories = subCats;

        setItems(newItems);
        dispatch(updateTpcCourseCategoryOrder({
            category: newItems,
            tpcCourseId: props.courseData.tpcCourseId,
            handle: props.userDetails.handle
        }));
    }
    else if (type === "LESSON") {
        const parseDroppable = (id) => {
          const parts = id.split("-");
          const catId = parts[1];
          const subId = parts.length === 3 ? parts[2] : null;
          return { catId, subId };
        };

        const sourceInfo = parseDroppable(source.droppableId);
        const destInfo = parseDroppable(destination.droppableId);

        let newItems = [...items];

        const sourceCatIndex = newItems.findIndex(c => String(c.id) === String(sourceInfo.catId));
        if (sourceCatIndex === -1) return;

        let movedLesson = null;

        if (sourceInfo.subId) {
          const sSubIndex = newItems[sourceCatIndex].subCategories.findIndex(
            sc => String(sc.id) === String(sourceInfo.subId)
          );
          if (sSubIndex === -1) return;

          let sourceLessons = Array.from(newItems[sourceCatIndex].subCategories[sSubIndex].lessons || []);
          [movedLesson] = sourceLessons.splice(source.index, 1);
          newItems[sourceCatIndex].subCategories[sSubIndex].lessons = sourceLessons;
        } else {
          let sourceLessons = Array.from(newItems[sourceCatIndex].lessons || []);
          [movedLesson] = sourceLessons.splice(source.index, 1);
          newItems[sourceCatIndex].lessons = sourceLessons;
        }

        if (!movedLesson) return;

        if (destInfo.subId) {
          movedLesson.subCategoryId = parseInt(destInfo.subId, 10);
        } else {
          delete movedLesson.subCategoryId;
        }

        const destCatIndex = newItems.findIndex(c => String(c.id) === String(destInfo.catId));
        if (destCatIndex === -1) return;

        if (destInfo.subId) {
          const dSubIndex = newItems[destCatIndex].subCategories.findIndex(
            sc => String(sc.id) === String(destInfo.subId)
          );
          if (dSubIndex === -1) return;

          let destLessons = Array.from(newItems[destCatIndex].subCategories[dSubIndex].lessons || []);
          destLessons.splice(destination.index, 0, movedLesson);
          destLessons.forEach((l, i) => l.id = i + 1);
          newItems[destCatIndex].subCategories[dSubIndex].lessons = destLessons;
        } else {
          let destLessons = Array.from(newItems[destCatIndex].lessons || []);
          destLessons.splice(destination.index, 0, movedLesson);
          destLessons.forEach((l, i) => l.id = i + 1);
          newItems[destCatIndex].lessons = destLessons;
        }

        if (sourceInfo.subId) {
          const sSubIndex = newItems[sourceCatIndex].subCategories.findIndex(
            sc => String(sc.id) === String(sourceInfo.subId)
          );
          if (sSubIndex !== -1) {
            newItems[sourceCatIndex].subCategories[sSubIndex].lessons.forEach((l, i) => l.id = i + 1);
          }
        } else {
          newItems[sourceCatIndex].lessons.forEach((l, i) => l.id = i + 1);
        }

        setItems(newItems);
        dispatch(
          updateTpcCourseCategoryOrder({
            category: newItems,
            tpcCourseId: props.courseData.tpcCourseId,
            handle: props.userDetails.handle
          })
        );
      }

  };

  // ---------- Helpers to Render Draggables & Droppables ----------
  function renderCategories() {
    return (
      <Droppable droppableId="droppable-categories" type="CATEGORY">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {items.map((cat, index) => (
              <Draggable
                key={`cat-${cat.id}`}
                draggableId={`cat-${cat.id}`}
                index={index}
              >
                {(providedDrag) => (
                  <div
                    ref={providedDrag.innerRef}
                    {...providedDrag.draggableProps}
                    {...providedDrag.dragHandleProps}
                    style={{
                      marginBottom: "16px",
                      ...providedDrag.draggableProps.style
                    }}
                  >
                    {renderSingleCategory(cat)}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }

  function renderSingleCategory(cat) {
    return (
      <Card className="shadow">
        <CardHeader className="border-0">
          <div className="row mb-3">
            <div className='col-lg-7'>
              <h6 className="text-uppercase text-muted ls-1 mb-1">
                Category {cat.id}
              </h6>
              <h2 className="mb-0">{cat.text}</h2>
            </div>
            <div className='col-lg-5 text-lg-right mt-lg-0 mt-3 mb-3 text-center'>
              <Button color="info" onClick={(e) => handleCreateLesson(e, cat, "category", cat.id)}>
                <i className="fas fa-plus-circle"></i> Lesson
              </Button>
              <Button color="primary" onClick={() => handleAddSubcategory(cat)}>
                <i className="fas fa-folder-plus"></i> Subcategory
              </Button>
              <Button color="warning" onClick={(e) => handleUpdateCategory(e, cat)}>
                <i className="fas fa-pen"></i>
              </Button>
              <Button color="danger" onClick={(e) => handleDeleteTpcCourseCategory(e, cat.id)}>
                <i className="fas fa-trash"></i>
              </Button>
            </div>
          </div>

          {renderLessons(cat.lessons, cat.id, null)}
          {renderSubcategories(cat)}
        </CardHeader>
      </Card>
    );
  }

  function renderSubcategories(cat) {
    if (!cat.subCategories || !cat.subCategories.length) return null;

    return (
      <Droppable droppableId={`subcats-${cat.id}`} type={`SUBCAT-${cat.id}`}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {cat.subCategories.map((sub, index) => (
              <Draggable
                key={`subcat-${cat.id}-${sub.id}`}
                draggableId={`subcat-${cat.id}-${sub.id}`}
                index={index}
              >
                {(providedDrag) => (
                  <div
                    ref={providedDrag.innerRef}
                    {...providedDrag.draggableProps}
                    {...providedDrag.dragHandleProps}
                    style={{
                      marginBottom: "8px",
                      ...providedDrag.draggableProps.style
                    }}
                  >
                    {renderSingleSubcategory(sub, cat.id)}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }

  function renderSingleSubcategory(subCat, parentCategoryId) {
    return (
      <Row className='mb-3'>
        <Col>
          <Card className="shadow">
            <CardHeader className="border-0">
              <div className='row mb-2'>
                <div className='col-lg-7'>
                  <h6 className="text-uppercase text-muted ls-1 mb-1">
                    Subcategory {subCat.id}
                  </h6>
                  <h2 className="mb-0">{subCat.text}</h2>
                </div>
                <div className='col-lg-5 text-lg-right mt-lg-0 mt-3 mb-3 text-center'>
                  <Button color="info" onClick={(e) => handleCreateLesson(e, subCat, "subcategory", parentCategoryId)}>
                    <i className="fas fa-plus-circle"></i>
                  </Button>
                  <Button color="warning" onClick={() => handleEditSubcategory(subCat, parentCategoryId)}>
                    <i className="fas fa-pen"></i>
                  </Button>
                  <Button color="danger" onClick={() => handleDeleteSubcategory(subCat, parentCategoryId)}>
                    <i className="fas fa-trash"></i>
                  </Button>
                </div>
              </div>

              {renderLessons(subCat.lessons, parentCategoryId, subCat.id)}
            </CardHeader>
          </Card>
        </Col>
      </Row>
    );
  }

  function renderLessons(lessonsArray, catId, subCatId) {
    const droppableKey = subCatId
      ? `lessons-${catId}-${subCatId}`
      : `lessons-${catId}`;

    return (
      <Droppable droppableId={droppableKey} type="LESSON">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps} style={{ minHeight: 50 }}>
            {(lessonsArray || []).map((lesson, index) => (
              <Draggable
                key={`lesson-${catId}-${subCatId || 0}-${lesson.id}`}
                draggableId={`lesson-${catId}-${subCatId || 0}-${lesson.id}`}
                index={index}
              >
                {(providedDrag) => (
                  <div
                    ref={providedDrag.innerRef}
                    {...providedDrag.draggableProps}
                    {...providedDrag.dragHandleProps}
                    style={{
                      marginBottom: '8px',
                      ...providedDrag.draggableProps.style
                    }}
                  >
                    {renderSingleLesson(lesson, catId, subCatId)}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }

  function renderSingleLesson(lesson, catId, subCatId) {
    const isSubcategory = !!subCatId;
    return (
      <Card className="shadow">
        <CardHeader className="border-0">
          <div className="row">
            <div className='col-lg-8'>
              <h6 className="text-uppercase text-muted ls-1 mb-1">
                Lesson {lesson.id} - {lesson.visibility === "draft"
                  ? <label className='text-warning'>{lesson.visibility}</label>
                  : <label className='text-success'>{lesson.visibility}</label>
                }
              </h6>
              <h2 className="mb-0">{lesson.title}</h2>
            </div>
            <div className='col-lg-4 text-lg-right mt-lg-0 mt-3 text-center'>
              <Button
                color="primary"
                onClick={(e) => handleUpdateLesson(e, lesson, catId, isSubcategory, subCatId)}
              >
                <i className="fas fa-pen"></i>
              </Button>
              <Button
                color="danger"
                onClick={(e) => handleDeleteTpcCourseLesson(e, lesson, catId, isSubcategory, subCatId)}
              >
                <i className="fas fa-trash"></i>
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  // ---------- Reset from Child Modals if needed ----------
  const handleCourseReset = (data) => {
    setItems(data);
  };

  // ---------- Render ----------
  return (
    <>
      <Row className='mb-3'>
        <Col md="12">
          <Card className="shadow">
            <CardHeader className="border-0">
              <div className='float-left'>
                <h6 className="text-uppercase text-muted ls-1 mb-1">
                  Video Course
                </h6>
                <h2 className="mb-0">{props.courseData.courseTitle}</h2>
              </div>
              <div className='float-right'>
                <Button color="primary" onClick={handleCreateCategory}>
                  Create Category
                </Button>
              </div>
            </CardHeader>
          </Card>
        </Col>
      </Row>

      <DragDropContext onDragEnd={onDragEnd}>
        <Row>
          <Col md="12">{renderCategories()}</Col>
        </Row>
      </DragDropContext>

      {/* Existing modals */}
      <AddTpcCourseCategoryModal
        modalAddTpcCourseCategoryModal={modalAddTpcCourseCategoryModal}
        toggleAddTpcCourseCategoryModal={toggleAddTpcCourseCategoryModal}
        editCourseCategory={editCourseCategory}
        courseData={courseProps}
        categoryToUpdate={categoryToUpdate}
        items={items}
      />
      <AddTpcCourseCategoryLessonsModal
        modalAddTpcCourseCategoryLessonsModal={modalAddTpcCourseCategoryLessonsModal}
        toggleAddTpcCourseCategoryLessonsModal={toggleAddTpcCourseCategoryLessonsModal}
        editCourseCategoryLessons={editCourseCategoryLessons}
        items={items}
        selectedCategory={selectedCategory}
        courseData={courseProps}
        lessonToUpdate={lessonToUpdate}
        categoryId={categoryId}
        handleCourseResetCallBack={handleCourseReset}
        createLessonTo={createLessonTo}
        parentCategoryId={parentCategoryId}
        subCategoryId={subCategoryId}
      />
      <AddTpcCourseSubCategoryModal
        modalAddTpcCourseSubcategory={modalAddTpcCourseSubcategory}
        toggleAddTpcCourseSubcategory={toggleAddTpcCourseSubcategoryModal}
        editSubcategory={editSubcategory}
        subcategoryToUpdate={subcategoryToUpdate}
        parentCategoryId={parentCategoryId}
        items={items}
        courseData={props.courseData}
      />
    </>
  );
};

/* ---------------------------------------
   Redux connections
--------------------------------------- */
const mapStateToProps = (state) => {
  return {
    userDetails: state.loginAuth.userDetails,
    createTpcCourseCategory: state.createTpcCourseCategory,
    fetchTpcCourses: state.fetchTpcCourses,
    deleteTpcCourseLessons: state.deleteTpcCourseLessons,
    deleteTpcCourseCategory: state.deleteTpcCourseCategory,
    activeSpecialty: state.specialty.activeSpecialty
  };
};

export default connect(mapStateToProps)(ManageTpcCoursesLessons);
